import styled, { createGlobalStyle } from "styled-components";
import { Flex, Box } from "rebass/styled-components";
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import { UserStateProvider } from "./userStore";
import { GetApp } from "./MVVM/Components/GetApp";
import { ClipQualityBubble } from "./MVVM/Components/ClipQualitySettings/ClipQualityBubble";
import { ClipQualitySettingsModalRenderer } from "./MVVM/Components/ClipQualitySettings/ClipQualitySettingsModalRenderer";
import { ModalManager } from "./MVVM/Components/ModalManager";
import { SiteNotice } from "./MVVM/Components/SiteNotice";
import { AppRoutes } from "./routes";
import { Navigation } from "./navigation";
import { Footer } from "./footer";
import { SiteHeader } from "./MVVM/Components/SiteHeader";
import { usePortal } from "MVVM/Hooks/usePortal";
import { Wrapped } from "MVVM/Views/Wrapped";
import { SmartBanner } from "./MVVM/Components/SmartBanner";

const Main = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const game = urlParams.get("game");
  const location = useLocation();
  const {
    portalOpen: wrappedPortalOpen,
    setPortalOpen: setWrappedPortalOpen,
    Portal: WrappedPortal,
  } = usePortal(<Wrapped period="2024" />, "root");

  const wrappedEnabled = useMemo(() => {
    //eslint-disable-next-line
    const wrappedRegExp = /^\/wrapped\/2024\/[^\/\s]+$/g;

    return wrappedRegExp.test(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (wrappedEnabled) setWrappedPortalOpen(true);
  }, [setWrappedPortalOpen, wrappedEnabled]);

  if (game) localStorage.setItem("game", game);
  if (window.document.referrer)
    localStorage.setItem("initialReferrer", window.document.referrer);

  useEffect(() => {
    document.getElementById("main").scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const segment = window.location.pathname.split("/")[1];
    document.body.classList.add(`page-${segment}`);
    return () => {
      document.body.classList.remove(`page-${segment}`);
    };
  }, [location]);

  return (
    <UserStateProvider>
      {wrappedPortalOpen && WrappedPortal}
      <GlobalStyle />
      <PageBackgroundStyle />
      {createPortal(
        <BottomRightStack>
          <ClipQualityBubble />
          <GetApp />
        </BottomRightStack>,
        document.body,
      )}
      <LayeredContent>
        <SmartBanner
          title="Allstar.gg"
          appMeta={{
            ios: "6476161266",
            android: "com.androidapp.gg.allstar",
          }}
          deepLinks={{
            ios: "allstar://",
            android: "allstar://",
          }}
          position="top"
          button="Open"
          storeText={{
            ios: "Clip like the pros",
            android: "Clip like the pros",
          }}
        />
        <SiteHeader />
        <ColumnWrapper>
          <LeftColumn>
            <Navigation />
          </LeftColumn>
          <MainColumn>
            <SiteNotice />
            <SiteContent id="sitecontent">
              <MainContent id="main">
                <AppRoutes />
                <Footer />
              </MainContent>
            </SiteContent>
          </MainColumn>
        </ColumnWrapper>
      </LayeredContent>
      <ModalManager />
      <ClipQualitySettingsModalRenderer />
    </UserStateProvider>
  );
};

const BottomRightStack = styled(Flex)`
  align-items: flex-end;
  bottom: 0;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 22px;
  padding-right: 20px;
  position: fixed;
  right: 0;
  z-index: 7;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    padding-bottom: 15px;
    padding-right: 15px;
  }

  @media (max-width: ${(props) => props.theme.breaks.narrow}) {
    display: none;
  }
`;

const ColumnWrapper = styled(Flex)`
  position: relative;
  height: 100%;
`;

const GlobalStyle = createGlobalStyle`
  html {
    @media (max-width: ${(props) => props.theme.breaks.narrow}) {
      font-size: 14px;
    }
  }
`;

const MainContent = styled(Box)`
  overflow-y: auto;
  width: 100%;
  &::-webkit-scrollbar {
    width: 12px; /* width of the entire scrollbar */
  }

  &::-webkit-scrollbar-track {
    background: #192832;
    border-left: 1px solid #20323f;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #253a49;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const LeftColumn = styled(Flex)`
  flex-shrink: 0;
  height: 100%;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    position: absolute;
    z-index: 6;
  }
`;

const MainColumn = styled(Flex)`
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const SiteContent = styled(Flex)`
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const PageBackgroundStyle = createGlobalStyle`
  body {
    background-color: #1c2a34;
    background-position: top;
    background-size: contain;
    background-repeat: no-repeat;
  }
  
  .page-mobile-app-link {
    background: rgb(12,19,24);
  }

  .page-reset {
    background: linear-gradient(90deg, rgba(26,40,48,1) 0%, rgba(26,42,51,1) 47%, rgba(24,37,45,1) 100%);
  }

  .page-overwolf {
    background: #0b0c18;
  }
`;

const LayeredContent = styled(Flex)`
  flex-direction: column;
  width: 100%;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    flex-direction: column;
  }
`;

export { Main };
